/**
 * useMultiAgentEditorBridge — the multi-agent-owned editor bridge (MA-6).
 *
 * PORTED from pages/dev-agent/devAgentPage/useDevAgentEditorBridge.ts, but with
 * every dev-agent session dependency removed:
 *   - useDevAgentStore.virtualFiles / acceptedCode / pendingFile  -> local state
 *     + useMultiAgentIdeStore.pendingRefactorFile (the multi-agent refactor target)
 *   - artifact open via dev-agent getMetadataItemById -> multi-agent getArtifact
 *   - editorOpen is owned by the panel via local useState (passed in)
 *
 * It seeds the editor's initial content from:
 *   - the multi-agent refactor target (pendingRefactorFile), and
 *   - any locally-seeded content the panel pushes via setSeededContent.
 * No dev-agent store, no useDevAgentSession.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import type { OpenFile } from "@/pages/dev-agent/components/MetadataEditorPanel";

/**
 * Extract editable CODE from an artifact's raw content. Many artifacts store a
 * JSON envelope (code_patch → {source:{original,replacement,unified_diff}}, or
 * {content}/{body}) rather than bare source — dumping that JSON into Monaco is
 * what made opened artifacts "show links/JSON" instead of the class. This digs
 * out the new/replacement body (falling back to original), and otherwise
 * returns the content untouched (already raw apex/diff).
 */
export function extractArtifactCode(raw: string): string {
  const text = raw || "";
  const trimmed = text.trim();
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return text;
  try {
    const obj = JSON.parse(trimmed) as Record<string, unknown>;
    const src = (obj.source && typeof obj.source === "object" ? obj.source : obj) as Record<string, unknown>;
    const pick = (...keys: string[]): string | null => {
      for (const k of keys) {
        const v = src[k] ?? obj[k];
        if (typeof v === "string" && v.trim()) return v;
      }
      return null;
    };
    // Prefer the NEW body for an editable file; fall back to original, then diff.
    const code =
      pick("replacement", "modified", "new_content", "content", "body", "code") ||
      pick("original") ||
      pick("unified_diff", "diff");
    return code ?? text;
  } catch {
    return text;
  }
}

interface MetadataEditorState {
  openFiles: OpenFile[];
  activeFileId: string | null;
  acceptedCode: string | null;
  initialContent: Record<string, string>;
  setInitialContent: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setAcceptedCode: (code: string | null) => void;
  /** Permanent open — a double-click, or anything the agent opens deliberately. */
  openTab: (entry: OpenFile) => void;
  /** Preview open — the sidebar's single click. Reuses the one preview slot. */
  previewTab: (entry: OpenFile) => void;
  handleCloseMetadataEditor: () => void;
  migrateFileId: (fromId: string, toId: string) => void;
}

interface UseMultiAgentEditorBridgeInput {
  editor: MetadataEditorState;
  editorOpen: boolean;
  setEditorOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

export function useMultiAgentEditorBridge({
  editor,
  editorOpen,
  setEditorOpen,
  setSidebarCollapsed,
}: UseMultiAgentEditorBridgeInput) {
  const [analyzingFile, setAnalyzingFile] = useState<string | null>(null);
  // Multi-agent refactor target — its content seeds the editor (replaces the
  // dev-agent virtualFiles seed). Keyed by the file id.
  const pendingRefactorFile = useMultiAgentIdeStore((s) => s.pendingRefactorFile);
  const workspaceCodeRequest = useMultiAgentIdeStore((s) => s.workspaceCodeRequest);

  const openFilesCount = editor.openFiles.length;

  // Open the editor whenever a tab exists; close it when the last tab is gone.
  useEffect(() => {
    if (editor.openFiles.length > 0 && !editorOpen) {
      setEditorOpen(true);
    }
  }, [editor.openFiles.length, editorOpen, setEditorOpen]);

  useEffect(() => {
    if (editorOpen && openFilesCount === 0) {
      const timeout = setTimeout(() => setEditorOpen(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [editorOpen, openFilesCount, setEditorOpen]);

  // Collapse the sidebar when the editor opens (dev-agent parity, sans rules.cls).
  useEffect(() => {
    if (editorOpen) setSidebarCollapsed(true);
  }, [editorOpen, setSidebarCollapsed]);

  // Seed editor initial content from the multi-agent refactor target.
  const mergedInitialContent = useMemo(() => {
    const merged: Record<string, string> = { ...editor.initialContent };
    if (pendingRefactorFile?.id && pendingRefactorFile.content && !merged[pendingRefactorFile.id]) {
      merged[pendingRefactorFile.id] = pendingRefactorFile.content;
    }
    return merged;
  }, [editor.initialContent, pendingRefactorFile]);

  /**
   * Open a file in the IDE editor.
   *
   * `preview` is the sidebar's single click: a tab the next single click reuses.
   * Everything else — a double-click, an artifact the agent produced, a refactor
   * target — is a deliberate open and therefore permanent.
   */
  const handleOpenFileInEditor = useCallback(
    (
      file: { id: string; name: string; metadataType?: string },
      { preview = false }: { preview?: boolean } = {},
    ) => {
      // Multi-agent artifact tabs (art_…) — resolve content via the multi-agent
      // artifact service instead of the dev-agent metadata service.
      if (file.id.startsWith("art_")) {
        void (async () => {
          try {
            const { getArtifact } = await import("@/services/multi-agent");
            const art = await getArtifact(file.id);
            editor.setInitialContent((prev) => ({
              ...prev,
              // Dig the actual code out of the artifact envelope so Monaco shows
              // the class, not a JSON blob / artifact links.
              [file.id]: extractArtifactCode(art.content || ""),
            }));
            editor.openTab({
              id: file.id,
              name: file.name,
              metadataType: file.metadataType || "ApexClass",
            });
            setEditorOpen(true);
          } catch (error) {
            console.warn("[multi-agent artifact-open] fetch failed", error);
          }
        })();
        return;
      }

      // Real metadata items load via the editor panel's own useMetadataItem.
      const entry = {
        id: file.id,
        name: file.name,
        metadataType: file.metadataType || "ApexClass",
      };
      if (preview) editor.previewTab(entry);
      else editor.openTab(entry);
      setEditorOpen(true);
    },
    [editor, setEditorOpen],
  );

  // Seed the editor when the multi-agent refactor target carries content.
  const lastSeededPendingRef = useRef<unknown>(null);
  useEffect(() => {
    if (!pendingRefactorFile?.content || !pendingRefactorFile.id) return;
    if (lastSeededPendingRef.current === pendingRefactorFile) return;
    lastSeededPendingRef.current = pendingRefactorFile;
    const fileId = String(pendingRefactorFile.id);
    editor.setInitialContent((previous) => ({
      ...previous,
      [fileId]: pendingRefactorFile.content,
    }));
    editor.openTab({
      id: fileId,
      name: pendingRefactorFile.name,
      metadataType: pendingRefactorFile.metadataType || "ApexClass",
    });
    setEditorOpen(true);
  }, [editor, pendingRefactorFile, setEditorOpen]);

  // Any generated-code Monaco surface (artifact card, impact/dependency panel)
  // can request the IDE to open an exact workspace tab. This mirrors dev-agent's
  // Add to Workspace / Compare behavior without coupling to devAgentStore.
  const lastWorkspaceRequestRef = useRef<string | null>(null);
  useEffect(() => {
    if (!workspaceCodeRequest?.id || !workspaceCodeRequest.content) return;
    // acceptedCode is legitimately "" for a delete (no new body BY DESIGN) — key
    // off != null so a delete's dedup key doesn't collapse into "open".
    const hasAcceptedCode = workspaceCodeRequest.acceptedCode != null;
    const key = `${workspaceCodeRequest.id}:${workspaceCodeRequest.content.length}:${hasAcceptedCode ? "compare" : "open"}:${workspaceCodeRequest.acceptedCodeOperation ?? ""}`;
    if (lastWorkspaceRequestRef.current === key) return;
    lastWorkspaceRequestRef.current = key;

    editor.setInitialContent((previous) => ({
      ...previous,
      [workspaceCodeRequest.id]: workspaceCodeRequest.content,
    }));
    editor.openTab({
      id: workspaceCodeRequest.id,
      name: workspaceCodeRequest.name,
      metadataType: workspaceCodeRequest.metadataType || "ApexClass",
    });

    const ide = useMultiAgentIdeStore.getState();
    ide.setArtifactBadge(workspaceCodeRequest.id, workspaceCodeRequest.badge);
    ide.setAcceptedCodeTarget(workspaceCodeRequest.id);
    ide.setAcceptedCodeOriginal(workspaceCodeRequest.acceptedCodeOriginal ?? null);
    ide.setAcceptedCodeOperation(workspaceCodeRequest.acceptedCodeOperation ?? null);
    // != null, not truthy: a delete's acceptedCode is "" BY DESIGN (no new body),
    // and that must still open the diff — a truthy check here silently dropped it.
    if (workspaceCodeRequest.acceptedCode != null) {
      editor.setAcceptedCode(workspaceCodeRequest.acceptedCode);
    }
    setEditorOpen(true);
    ide.setWorkspaceCodeRequest(null);
  }, [editor, setEditorOpen, workspaceCodeRequest]);

  const handleCloseEditor = useCallback(() => {
    setEditorOpen(false);
    editor.handleCloseMetadataEditor();
  }, [editor, setEditorOpen]);

  const handleLoadIntoChat = useCallback(
    (payload: {
      id: string;
      name: string;
      metadataType?: string;
      content?: string;
    }) => {
      const fileId = payload.id;
      const content =
        payload.content ||
        editor.initialContent[fileId] ||
        editor.initialContent[payload.name] ||
        "";
      // Seed the class as the next run's refactor target (UUID + name + source),
      // so "Load into chat" puts it in the multi-agent context.
      useMultiAgentIdeStore.getState().setPendingRefactorFile({
        id: fileId,
        name: payload.name,
        content: content || "",
        metadataType: payload.metadataType,
      });
    },
    [editor.initialContent],
  );

  return {
    analyzingFile,
    setAnalyzingFile,
    mergedInitialContent,
    handleOpenFileInEditor,
    handleCloseEditor,
    handleLoadIntoChat,
  };
}
